import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db, auth } from "../../firebase/firebase";
import { FaBookmark, FaRegBookmark, FaShare } from "react-icons/fa";
import { useAuthState } from "react-firebase-hooks/auth";
import API_BASE_URL from "../../api/config";

// Hardcoded default shlok to display if API loading fails
const DEFAULT_SHLOK = {
  chapter: "2",
  verse: "47",
  sanskrit:
    "कर्मण्येवाधिकारस्ते मा फलेषु कदाचन | मा कर्मफलहेतुर्भूर्मा ते सङ्गोऽस्त्वकर्मणि ||२-४७||",
  transliteration:
    "karmaṇy-evādhikāras te mā phaleṣhu kadāchana mā karma-phala-hetur bhūr mā te saṅgo 'stv akarmaṇi",
  english_meaning:
    "You have a right to perform your prescribed duties, but you are not entitled to the fruits of your actions. Never consider yourself to be the cause of the results of your activities, and never be attached to inaction.",
  application:
    "Focus on doing your best work without being attached to results. This approach reduces anxiety and promotes excellence through detachment.",
};

// Add local storage key for caching
const SHLOK_CACHE_KEY = "tamohar_daily_shlok";
const SHLOK_CACHE_DATE_KEY = "tamohar_daily_shlok_date";

const DailyShlok = () => {
  const [shlok, setShlok] = useState(null); // Start with null instead of default
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [note, setNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [user] = useAuthState(auth);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDailyShlok = async () => {
      try {
        setLoading(true);

        // Check if we have today's shlok in localStorage
        const today = new Date();
        const dateString = `${today.getFullYear()}-${
          today.getMonth() + 1
        }-${today.getDate()}`;
        const cachedDate = localStorage.getItem(SHLOK_CACHE_DATE_KEY);

        // If we have a cached shlok for today, use it immediately
        if (cachedDate === dateString) {
          const cachedShlok = JSON.parse(
            localStorage.getItem(SHLOK_CACHE_KEY) || "null"
          );
          if (cachedShlok) {
            console.log("Using cached shlok from localStorage");
            setShlok(cachedShlok);

            // We still need to check if it's bookmarked, but we can show content immediately
            if (user) {
              checkIfBookmarked(cachedShlok);
            }

            setLoading(false);
            return;
          }
        }

        // If no cache or expired, fetch from API
        try {
          const response = await fetch(`${API_BASE_URL}/api/shloks/daily`);

          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }

          const data = await response.json();
          if (data.success && data.shlok) {
            const todayShlok = data.shlok;
            console.log(`Loaded daily shlok for ${dateString}`);

            // Cache the shlok in localStorage
            localStorage.setItem(SHLOK_CACHE_KEY, JSON.stringify(todayShlok));
            localStorage.setItem(SHLOK_CACHE_DATE_KEY, dateString);

            setShlok(todayShlok);

            // Check if bookmarked
            if (user) {
              checkIfBookmarked(todayShlok);
            }
          } else {
            throw new Error("No daily shlok data in API response");
          }
        } catch (apiError) {
          console.warn("Error fetching from API:", apiError);

          // Use default shlok instead of complex CSV parsing fallback
          console.log("Using default shlok");
          setShlok(DEFAULT_SHLOK);
        }
      } catch (err) {
        console.error("Error in shlok loading process:", err);
        setError(err.message);
        setShlok(DEFAULT_SHLOK); // Use default on any error
      } finally {
        setLoading(false);
      }
    };

    // Separate function to check if a shlok is bookmarked
    const checkIfBookmarked = async (currentShlok) => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const bookmarks = userData.bookmarks || [];

          const isCurrentShlokBookmarked = bookmarks.some(
            (bookmark) =>
              bookmark.chapter === currentShlok.chapter &&
              bookmark.verse === currentShlok.verse
          );

          setIsBookmarked(isCurrentShlokBookmarked);
        }
      } catch (err) {
        console.error("Error checking bookmark status:", err);
      }
    };

    fetchDailyShlok();
  }, [user]);

  const handleBookmark = async () => {
    if (!user) {
      alert("Please sign in to bookmark shloks");
      return;
    }

    if (isBookmarked) {
      try {
        // Find the bookmark to remove
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();
        const bookmarks = userData.bookmarks || [];

        const updatedBookmarks = bookmarks.filter(
          (bookmark) =>
            !(
              bookmark.chapter === shlok.chapter &&
              bookmark.verse === shlok.verse
            )
        );

        await updateDoc(doc(db, "users", user.uid), {
          bookmarks: updatedBookmarks,
        });

        setIsBookmarked(false);
      } catch (error) {
        console.error("Error removing bookmark:", error);
      }
    } else {
      setShowNoteInput(true);
    }
  };

  const saveBookmarkWithNote = async () => {
    try {
      const userRef = doc(db, "users", user.uid);

      // Create the bookmark object
      const bookmark = {
        chapter: shlok.chapter,
        verse: shlok.verse,
        sanskrit: shlok.sanskrit,
        transliteration: shlok.transliteration,
        english_meaning: shlok.english_meaning,
        application: shlok.application,
        note: note,
        timestamp: new Date(),
      };

      // Add the bookmark to the user's bookmarks array
      await updateDoc(userRef, {
        bookmarks: arrayUnion(bookmark),
      });

      setIsBookmarked(true);
      setShowNoteInput(false);
      setNote("");
    } catch (error) {
      console.error("Error saving bookmark:", error);
    }
  };

  const shareVerse = async () => {
    const shareText = `Bhagavad Gita - Chapter ${shlok.chapter}, Verse ${shlok.verse}\n\n${shlok.sanskrit}\n\n${shlok.english_meaning}\n\nFrom Tamohar App`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Daily Wisdom from Bhagavad Gita",
          text: shareText,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback for browsers that don't support the Web Share API
      navigator.clipboard.writeText(shareText);
      alert("Verse copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="shlok-container">
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading today's wisdom...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error loading today's shlok: {error}</div>;
  }

  if (!shlok) {
    return <div className="error">Failed to load today's shlok.</div>;
  }

  return (
    <div className="shlok-container">
      <div className="date-banner">
        <h3>
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </h3>
      </div>

      <div className="shlok-card">
        <div className="shlok-header">
          <h2>Today's Wisdom</h2>
          <span className="chapter-verse">
            Chapter {shlok?.chapter || ""}, Verse {shlok?.verse || ""}
          </span>
        </div>

        <div className="sanskrit-text">
          <div className="sanskrit-heading">
            <h3>Shlok</h3>
            <div className="decorative-line"></div>
          </div>
          <p>{shlok?.sanskrit || ""}</p>
        </div>

        <div className="transliteration">
          <p>
            <em>{shlok?.transliteration || ""}</em>
          </p>
        </div>

        <div className="meaning">
          <div className="section-heading">
            <h3>Meaning</h3>
            <div className="decorative-line"></div>
          </div>
          <p>{shlok?.english_meaning || ""}</p>
        </div>

        <div className="application">
          <div className="section-heading">
            <h3>Life Application</h3>
            <div className="decorative-line"></div>
          </div>
          <p>{shlok?.application || ""}</p>
        </div>

        <div className="shlok-actions">
          <button
            className="action-button bookmark-button"
            onClick={handleBookmark}
            aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
          >
            {isBookmarked ? <FaBookmark /> : <FaRegBookmark />}
            <span>{isBookmarked ? " Bookmarked" : " Bookmark"}</span>
          </button>

          <button className="action-button share-button" onClick={shareVerse}>
            <FaShare />
            <span>Share</span>
          </button>
        </div>

        {showNoteInput && (
          <div className="note-input-container">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why does this shlok resonate with you? (optional)"
              rows={3}
            />
            <div className="note-actions">
              <button
                onClick={() => setShowNoteInput(false)}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={saveBookmarkWithNote}
                className="save-note btn-primary"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {user && isBookmarked && (
          <div className="bookmark-info">
            <p>This verse is in your bookmarks</p>
          </div>
        )}
      </div>

      <div className="inspiration-note">
        <p>
          "When you find your path, you must not be afraid. You need to have
          sufficient courage to make mistakes."
        </p>
        <span className="quote-author">— Spiritual wisdom</span>
      </div>
    </div>
  );
};

export default DailyShlok;
